import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { RecordingService } from './recording.service';

/**
 * Decides whether an announcement may interrupt the user right now.
 *
 * `tasks/announcements.md` — Phase E2. This service IS the "informative but not intrusive"
 * requirement. It is written as one small, synchronous, directly testable predicate precisely
 * because a requirement phrased as an intention erodes; phrased as a function with a harness
 * against it, it does not.
 *
 * ── THE FOUR RULES ───────────────────────────────────────────────────────────────────
 *
 * 1. **Never during a consultation.** `RecordingService` is a `providedIn: 'root'` singleton, so
 *    `isRecording()` is the truth for the whole app. A dialog opening over an ambient recording is
 *    the single worst thing this feature could do.
 *
 * 2. **Never over another dialog.** Material tracks every open dialog in `MatDialog.openDialogs`,
 *    so this covers the patient file, consent flows, the AI assistant and everything else that
 *    opens one — without this service having to know any of them exist.
 *
 * 3. **Only on the landing route.** `/patients/schedule` and nothing else. `/patients/list` is
 *    DELIBERATELY excluded: it is the ward-round screen, and a clinician working down a list of
 *    inpatients is mid-task even though the URL has not changed. That exclusion is a product
 *    decision rather than an obvious one, which is why it is named here and given its own test.
 *
 * 4. **`critical` is exempt from all of the above.** It is the one type allowed to interrupt,
 *    because the case it exists for ("results for scheme X may be misattributed, do not action")
 *    is worse to withhold than to interrupt with.
 *
 * ── THE SUPPRESSION COUNTER ──────────────────────────────────────────────────────────
 *
 * `suppress()` / `release()` let any future long-running workflow opt out without this file
 * learning about it. Paired like a lock: a caller that suppresses must release, including on the
 * error path, or announcements stop appearing for the rest of the session with nothing to show why.
 */
@Injectable({
    providedIn: 'root',
})
export class AnnouncementGateService {

    /**
     * The one route on which an announcement may open a dialog.
     *
     * Matched by prefix so query parameters and trailing segments do not defeat it.
     */
    private static readonly ALLOWED_ROUTES = ['/patients/schedule'];

    /** Nesting depth of `suppress()` calls. Zero means nothing is asking for quiet. */
    private suppressions = 0;

    constructor(
        private recording: RecordingService,
        private dialog: MatDialog,
        private router: Router,
    ) { }

    /**
     * Whether a NON-critical announcement may open a dialog right now.
     *
     * @param isCritical when true, bypasses every rule — see rule 4.
     */
    canInterrupt(isCritical: boolean = false): boolean {
        if (isCritical) return true;

        if (this.suppressions > 0) return false;
        if (this.recording.isRecording()) return false;
        if (this.dialog.openDialogs && this.dialog.openDialogs.length > 0) return false;

        return this.isOnAllowedRoute();
    }

    /** True when the current URL is one the dialog is allowed to appear over. */
    isOnAllowedRoute(): boolean {
        const url = (this.router.url || '').split('?')[0];
        return AnnouncementGateService.ALLOWED_ROUTES.some(
            (allowed) => url === allowed || url.startsWith(`${allowed}/`),
        );
    }

    /** Ask for quiet. Must be paired with `release()`, including on the error path. */
    suppress(): void {
        this.suppressions += 1;
    }

    /** Release one `suppress()`. Never goes below zero — an unpaired release is not a licence. */
    release(): void {
        this.suppressions = Math.max(0, this.suppressions - 1);
    }

    /** Exposed for the harness, so "is anything suppressing?" is assertable. */
    get isSuppressed(): boolean {
        return this.suppressions > 0;
    }
}
